import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { getDisplayName } from '../../../models/NameUtils';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { DeleteConfirmDialog } from '../../../web/components-v2/DeleteConfirmDialog';
import { Label } from '../../../web/components-v2/Label';
import blitzPlay from '../../assets/blitzPlay.png';
import { useResponseHandler } from '../../hooks/error';
import { LeadRuleViewModel, LeadRulesViewModel } from '../../viewModels/leadrules';
import { ownershipLabels, sortCriteriaOptions } from '../LeadRuleModal/utils';
import { RuleName } from './components/RuleName';
import { RuleOptions } from './components/RuleOptions';
import { RulePriority } from './components/RulePriority';
import { RuleRegularLabel } from './components/RuleRegularLabel';
import { RuleSchedule } from './components/RuleSchedule';
import { RuleTags } from './components/RuleTags';
import { styleSheet } from './styles';

interface IProps {
	isPlayable?: boolean;
	isReadonly?: boolean;
	leadRule: LeadRuleViewModel;
	leadRules?: LeadRulesViewModel | LeadRuleViewModel[];
	onClickToClone?(): void;
	onClickToDelete?(): void;
	onClickToEdit?(): void;
	onClickToEditSchedule?(): void;
	onClickToPlayBlitz?(): void;
}

export const LeadRule: React.FC<IProps> = observer(
	({
		isPlayable,
		isReadonly,
		leadRule,
		leadRules,
		onClickToClone,
		onClickToDelete,
		onClickToEdit,
		onClickToEditSchedule,
		onClickToPlayBlitz,
	}) => {
		const [leadUpdateError] = useResponseHandler('LeadUpdateEnablement', "I couldn't update the lead filter!");
		const [isDeleteOpen, setIsDeleteOpen] = useState(false);

		const toggle = () => {
			leadRule.setEnabled(!leadRule.isEnabled);
			leadRule
				.update()
				.catch(leadUpdateError)

				.finally(() => leadRules.sort());
		};

		if (!leadRule) {
			return null;
		}

		const onForText = () => {
			if (leadRule?.users?.length > 2) {
				return (
					leadRule.users
						.slice(0, 1)
						.map(x => getDisplayName(x))
						.join(', ') + ` and ${leadRule.users.length - 1} others`
				);
			} else if (leadRule?.users?.length) {
				return leadRule.users.map(x => getDisplayName(x)).join(', ');
			} else if (leadRule?.verticals?.length) {
				return leadRule.verticals.join(', ');
			} else {
				return 'Everyone';
			}
		};

		const ownership = ownershipLabels[leadRule?.ownershipEnforcement] || 'None';

		const handleDelete = () => {
			setIsDeleteOpen(true);
		};

		const handleDeleteConfirm = () => {
			setIsDeleteOpen(false);
			onClickToDelete?.();
		};

		const handleEdit = () => {
			onClickToEdit?.();
		};

		const handleEditSchedule = () => {
			onClickToEditSchedule?.();
		};

		const handleClone = () => {
			onClickToClone?.();
		};

		const isAlwaysOn = leadRule?.recurrence === 'AlwaysOn';

		const andTags = leadRule?.filterToArrays?.and;
		const orTags = leadRule?.filterToArrays?.or;
		const notTags = leadRule?.filterToArrays?.not;

		const outcomes = leadRule?.filterToArrays?.outcomes;

		const hasTags = andTags?.length > 0 || orTags?.length > 0 || notTags?.length > 0 || outcomes?.length > 0;

		// Configuration details for display
		const minDaysValue = leadRule?.minDaysSinceLastInteraction;
		const hourlyLimitValue = leadRule?.hourlyLimit;
		const isTemplate = leadRule?.preserve === true;
		const sortCriteriaValue = leadRule?.primarySortCriteria ?? 'LastInteraction';
		const sortLabel =
			sortCriteriaOptions.find(opt => opt.dataContext === sortCriteriaValue)?.text || 'Default (Oldest First)';

		const renderCustomRuleDisable = <div className={css(styleSheet.customRule)}>Custom Rule - Editing Disabled</div>;

		return (
			<div className={`${css(styleSheet.container, isPlayable && styleSheet.containerPlayable)} lead-rule`}>
				{leadRule.isCustomRule && renderCustomRuleDisable}

				<div className={css(styleSheet.priority)}>
					<RulePriority value={leadRule.priority} />
				</div>
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.titleRow)}>
						<RuleName name={leadRule.name} />
						<RuleRegularLabel title='For:' label={onForText()} />
						<RuleRegularLabel title='Ownership:' label={ownership} />
						{!isReadonly && !leadRule.isCustomRule && !isPlayable ? (
							<Label styles={[styleSheet.activeLabel]}>
								<Checkbox checked={leadRule.isEnabled} onCheckedChange={toggle} />
								Active
							</Label>
						) : null}
						{!leadRule.isCustomRule && !isPlayable ? (
							<RuleOptions
								onClickToClone={!isReadonly ? handleClone : null}
								onClickToDelete={!isReadonly ? handleDelete : null}
								onClickToEdit={!isReadonly ? handleEdit : null}
								onClickToEditSchedule={handleEditSchedule}
							/>
						) : null}
					</div>
					{!isReadonly && (
						<div className={css(styleSheet.details)}>
							{minDaysValue && (
								<RuleRegularLabel title='Min days:' label={`${minDaysValue} day${minDaysValue > 1 ? 's' : ''}`} />
							)}
							{hourlyLimitValue && <RuleRegularLabel title='Max leads/hr:' label={`${hourlyLimitValue}`} />}
							{isTemplate && <RuleRegularLabel title='Template:' label='Yes' />}
							<RuleRegularLabel title='Sort:' label={sortLabel} />
						</div>
					)}
					{!isReadonly && hasTags ? <RuleTags leadRule={leadRule} /> : null}
					{!isAlwaysOn && <RuleSchedule leadRule={leadRule} />}
				</div>
				{isPlayable && (
					<Button
						variant='ghost'
						size='icon'
						aria-label='Play Blitz'
						styles={[styleSheet.blitzPlayButton]}
						onClick={onClickToPlayBlitz}
					>
						<img className={css(styleSheet.blitzPlayImage)} src={blitzPlay} alt='' />
					</Button>
				)}
				<DeleteConfirmDialog
					open={isDeleteOpen}
					title='Delete Blitz?'
					description={`This permanently deletes ${leadRule.name}.`}
					onClose={() => setIsDeleteOpen(false)}
					onConfirm={handleDeleteConfirm}
				/>
			</div>
		);
	}
);
